import React, { Component } from 'react';
import queryString from 'query-string';

import BasePlayer from '../base-player/base-player';
import BlockingRender from '../blocking-render/blocking-render';
import TaggingTranscription from '../tagging-transcription/tagging-transcription';
import ChatSidebar from '../chat-sidebar/chat-sidebar';

import defaultProps from './default-props';
import propTypes from './prop-types';

import iframeToState from '../helpers/iframe-to-state';
import legacyToState from '../helpers/legacy-to-state';
import playlistToState from '../helpers/playlist-to-state';
import taggingToState from '../helpers/tagging-to-state';
import chatToState from '../helpers/chat-to-state';
import getVisualizerSettings from '../helpers/get-visualizer-settings';
import vdmToState from '../helpers/vdm-to-state';
import { testRestrictions, geoip2_country, geo_html5_country } from '../helpers/playback-restrictions';

import { testLoadAPI } from '../helpers/tests';
// import tryUpgrade from '../helpers/try-upgrade';

const displayName = 'Visualizer';

/**
 *  Este componente es la raiz del embebido (iframe), carga la informacion inicial, y decide que elementos deben mostrarse.
 */
class Visualizer extends Component {
  /**
   *  El constructor inicializa un elemento de carga para mostrar hasta que hayan datos.
   */
  constructor(props) {
    super(props);
    this.state = {
      visualizerType: 'Loading',
    };
  }
  /**
   *  Antes de montar el componente, se carga la informacion inicial (en legacy se carga toda y se guarda en estado para uso posterior),
   *  aqui se decide que elementos se deben mostrar.
   */
  componentWillMount() {
    const evaluateBooleanParameter = ( parameter) => {
      return parameter !== null ? parameter === 'true' || parameter ==='1' : true;
    };
    const queryParams = queryString.parse( this.props.location.search);
    const setQueryOptions = ( settings) => {
      if( '360' in queryParams) {   // TODO: parametrizar correctamente el modo 360 en los json.
        settings.playlist[0].stereomode = queryParams['360'] ? queryParams['360'] : 'monoscopic';
      }
      if( 'controls' in queryParams) {
        settings.customProps.controls = evaluateBooleanParameter( queryParams.controls);
      }
      if( 'autoplay' in queryParams) {
        settings.customProps.autostart = evaluateBooleanParameter( queryParams.autoplay);
      }
      if( 'autostart' in queryParams) {
        settings.customProps.autostart = evaluateBooleanParameter( queryParams.autostart);
      }
      if( 'force_autoplay' in queryParams) {
        settings.customProps.force_autoplay = evaluateBooleanParameter( queryParams.force_autoplay);
      }
      if( 'loop' in queryParams) {
        settings.customProps.repeat = evaluateBooleanParameter( queryParams.loop);
      }
      if( 'repeat' in queryParams) {
        settings.customProps.repeat = evaluateBooleanParameter( queryParams.repeat);
      }
      if( 'mute' in queryParams) {
        settings.customProps.mute = evaluateBooleanParameter( queryParams.mute);
      }
      if( 'injectSrc' in queryParams || 'injectsrc' in queryParams) {
        settings.customProps.injectSrc = queryParams.injectSrc || queryParams.injectsrc;   // TODO: injectar directamente en playlist[0].file, pendiente de (testAndChooseSrc - TODO)
      }
      if( 'miniature' in queryParams) {
        settings.customProps.image = queryParams.miniature;    // TODO: injectar directamente en playlist[0].image, pendiente de (testAndChooseImage - TODO)
      }
      if( 'disableManualSeek' in queryParams) {
        settings.basePlayerOptions.disableManualSeek = evaluateBooleanParameter( queryParams.disableManualSeek);
      }
      if( 'startFromSecond' in queryParams) {
        settings.basePlayerOptions.startFromSecond = queryParams.startFromSecond ? parseInt( queryParams.startFromSecond, 10) : 0;
      }
      if( 'defaultLanguage' in queryParams) {
        settings.basePlayerOptions.defaultLanguage = queryParams.defaultLanguage;
      }
      if( 'defaultHiddenChat' in queryParams) {
        settings.basePlayerOptions.defaultHiddenChat = evaluateBooleanParameter( queryParams.defaultHiddenChat);
        if( settings.basePlayerOptions.defaultHiddenChat && settings.chatSettings && settings.chatSettings.onShowHide) {
          settings = settings.chatSettings.onShowHide( settings);
        }
      }
      if( 'token' in queryParams) {
        settings.basePlayerOptions.token = queryParams.token;
      }
      if( 'demo-backup' in queryParams) {
        let list = [
          'https://cdnlive.codev8.net/pruebaitlive/smil:channel3.smil/playlist.m3u8',
          'https://cdnlive.codev8.net/pruebaitlive/smil:channel2.smil/playlist.m3u8',
        ];
        switch( queryParams['demo-backup']) {
          case 'redsquare':
            list = [
              'https://cdnlive.codev8.net/redsquarelive/smil:channel1.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/redsquarelive/smil:channel2.smil/playlist.m3u8',
            ];
            break;
          case 'redsquare2':
            list = [
              'https://cdnlive.codev8.net/redsquarelive/smil:channel3.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/redsquarelive/smil:channel4.smil/playlist.m3u8',
            ];
            break;
          case 'redsquare3':
            list = [
              'https://cdnlive.codev8.net/redsquarelive/smil:channel5.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/redsquarelive/smil:channel6.smil/playlist.m3u8',
            ];
            break;
          case 'redsquare4':
            list = [
              'https://cdnlive.codev8.net/redsquarelive/smil:channel7.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/redsquarelive/smil:channel8.smil/playlist.m3u8',
            ];
            break;
          default:;
        }
        settings.basePlayerOptions.demoBackup = {
          current: 0,
          list,
        };
      }
      if( 'multi-channel' in queryParams) {
        let list = [
          'https://cdnlive.codev8.net/pruebaitlive/smil:channel3.smil/playlist.m3u8',
          'https://cdnlive.codev8.net/pruebaitlive/smil:channel2.smil/playlist.m3u8',
        ];
        let iconList = [
          '/vdmplayer/resources/facetime-button.png',
          '/vdmplayer/resources/facetime-button.png'
        ];
        switch( queryParams['multi-channel']) {
          case 'redsquare':
            list = [
              'https://cdnlive.codev8.net/redsquarelive/smil:channel5.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/redsquarelive/smil:channel6.smil/playlist.m3u8',
            ];
            iconList = [
              '/vdmplayer/resources/flags/uk.svg',
              '/vdmplayer/resources/flags/es.svg',
            ];
            break;
          case 'demojaime':
            list = [
              'https://cdnlive.codev8.net/demojaimelive/smil:channel1.smil/playlist.m3u8',
              'https://cdnlive.codev8.net/demojaimelive/smil:channel2.smil/playlist.m3u8',
            ];
            iconList = [
              '/vdmplayer/resources/flags/uk.svg',
              '/vdmplayer/resources/flags/es.svg',
            ];
            break;
          default:;
        }
        settings.basePlayerOptions.demoMultiChannel = {
          current: 0,
          list,
          iconList,
        };
      }
      if( 'lowLatency' in queryParams) {
        window.custom_jwplayer_hlsjs_liveSyncDuration = queryParams.lowLatency ? parseInt( queryParams.lowLatency, 10) : 4;
        settings.basePlayerOptions.lowLatency = true;
      }
      settings.queryParams = queryParams;
      // console.log( 'queryParams - ', settings); // DEVELOP
      return settings;
    }
    const params = this.props.match.params;
    const loadSettingsFunctions = {
      embed: params.id.length === 36 ? iframeToState : legacyToState,   // uuid4.length === 36
      tagging: taggingToState,
      playlist: playlistToState,
      chat: chatToState,
      watch: getVisualizerSettings,
      // vdmplayer: (id1,id2) => {
      //   if( id1 === 'geotest' ) {
      //     return geoip2_country().then( maxmind_location => {
      //       return geo_html5_country().then( html5_location => {
      //         return { visualizerType: 'geotest', maxmind_location: maxmind_location.response, html5_location};
      //       });
      //     });
      //   } else {
      //     return vdmToState( id1, id2);
      //   }
      // },
      vdmplayer: vdmToState,
    };
    // if( queryParams.forceLegacy === undefined) tryUpgrade( params.mode, params.id, params.id2);
    const setSettings = () => {
      if( this.refreshInterval) clearInterval( this.refreshInterval);
      let promiseOfSettings = Promise.resolve( { visualizerType: 'Unknown'});
      if( params.mode in loadSettingsFunctions){
        promiseOfSettings = loadSettingsFunctions[ params.mode]( params.id, params.id2, queryParams);
      }
      promiseOfSettings.then( (settings) => {
        testRestrictions( settings).then( (settings) => {
          settings.visualizerOptions = Object.assign( {}, settings.visualizerOptions, { location: this.props.location})
          this.setState( setQueryOptions( settings));
          if( settings.testSettingsForRefresh) this.refreshInterval = setInterval( () => {
            settings.testSettingsForRefresh().then( changed => {
              if( changed) {
                setSettings();
              }
            });
          }, 5000);
        });
      });
    }
    setSettings();
    // testLoadAPI();
  }
  componentWillUnmount() {
      // TODO: Pendiente de cancelar las promesas, emitira un warning si se desmonta el componenete antes de que se cumplan.
  }
  /**
   *  Se renderizan los elementos que correspondan.
   */
  render() {
    // console.log( 'Visualizer - render - props: ', this.props, ', state: ', this.state);     // DEVELOP
    if( this.state.visualizerOptions && this.state.visualizerOptions.onResize){
      window.onresize = () => {
        if( window.innerWidth !== this.state.customProps.width || window.innerHeight !== this.state.customProps.height) {
          this.setState( this.state.visualizerOptions.onResize( this.state));
        }
      };
      setTimeout( window.onresize, 500);
    } else {
      window.onresize = undefined;
    }
    switch( this.state.visualizerType) { // TODO: nuevo; mejorar la imagen (y/o mensaje) customizada de bloqueo; mejorar el mensaje de carga
      case 'Live':
      case 'Vod':
      case 'Playlist':
      case 'Vdm':
        return <BasePlayer {...this.state} visualizer={this} />;
      case 'Tagging':
        return (
          <div className='tagging player-container'>
            <BasePlayer {...this.state} getPlayer={ ( player, basePlayer) => this.setState( { player: player, basePlayer: basePlayer})} />
            <TaggingTranscription {...this.state} />
          </div>
        );
      case 'Chat':
        return (
          <div className='chat player-container'>
            <BasePlayer {...this.state} visualizer={this}/>
            <ChatSidebar {...this.state} />
          </div>
        );
      case 'Block':
        return <BlockingRender {...this.state} visualizer={this} />;
      case 'Loading':
        // return <li> Cargando... </li>;    // DEVELOP
        return null;
      case 'geotest':
        return (
          <div>
            <label>maxmind_continent: {this.state.maxmind_location.continent.code} - names: {JSON.stringify(this.state.maxmind_location.continent.names)}</label><br/>
            <label>maxmind_country: {this.state.maxmind_location.country.iso_code} - names: {JSON.stringify(this.state.maxmind_location.country.names)}</label><br/>
            <label>maxmind_city - names: {JSON.stringify(this.state.maxmind_location.city.names)}</label><br/>
            <label>maxmind_subdivisions:</label><br/>
            <div>
              { this.state.maxmind_location.subdivisions.map( (s,i) => (
                <div key={i}><label>{s.iso_code} - names: {JSON.stringify(s.names)}</label><br/></div>
              ))}
            </div>
            <label>maxmind_postal: {this.state.maxmind_location.postal.code}</label><br/>
            <label>maxmind_traits: {JSON.stringify(this.state.maxmind_location.traits)}</label><br/>
            <label>maxmind_location: {JSON.stringify(this.state.maxmind_location)}</label><br/>
            <label>html5_location latitude: {this.state.html5_location.latitude}</label><br/>
            <label>html5_location longitude: {this.state.html5_location.longitude}</label><br/>
            <label>html5_google country: {this.state.html5_location.country}</label><br/>
            <label>html5_google aal_1: {this.state.html5_location.aal_1}</label><br/>
            <label>html5_google aal_2: {this.state.html5_location.aal_2}</label><br/>
            <label>html5_google city: {this.state.html5_location.city}</label><br/>
            <label>html5_google street: {this.state.html5_location.street}</label><br/>
            <label>html5_google results: {JSON.stringify(this.state.html5_location.results)}</label><br/>
          </div>
        );
      default:
        // return <li> La ruta utilizada no se reconoce como un player valido, por favor asegurese de haberla escrito correctamente. </li>;   // DEVELOP
        return null;
    }
  }
}

Visualizer.defaultProps = defaultProps;
Visualizer.displayName = displayName;
Visualizer.propTypes = propTypes;
export default Visualizer;



// WEBPACK FOOTER //
// ./src/visualizer/visualizer.js